// @flow
import React, { useEffect } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import { sanitizeRouteParams } from 'metaup/routing/routingUtils';
import type { Routes } from 'metaup/routing/routingUtils';
import { listBrands, getBrand } from '../library/model/Brand.client';
import { getDevice } from '../library/model/Device.client';
import ErrorCapsule from '../core/exceptions/ErrorCapsule';

import CutPageBrandsView from './views/CutPageBrandsView';
import type { Brand } from '../library/model/Brand.model';
import type { Device } from '../library/model/Device.model';
import Loader from '../elements/Loader';
import LoadFailed from '../elements/LoadFailed';
import CutPageDevicesView from './views/CutPageDevicesView';
import CutPageWorkView from './views/CutPageWorkView';
import Error404Page from '../statics/Error404Page';
import { createCut } from '../print/model/Cut.client';
import type { Cut } from '../print/model/Cut.model';
import { getSelectedPrinter } from './SelectPrinterWidget';

// Стейт разделён по типам данных. Каждый загружается отдельно по необходимости.
type CutState = {
  brands: null | Array<Brand> | ErrorCapsule,
  brandData: null | Brand | ErrorCapsule,
  brandDataId: ?string,
  deviceData: null | Device | ErrorCapsule,
  deviceDataId: ?string,
  selectedVariantId: ?string,
  currentCut: ?Cut,
}

// === Shape для каждого этапа — грузим только то что нужно ===

// Этап 1: список брендов с логотипами (без устройств)
const brandsListShape = `{
  id
  name
  logo {
    id
    fit(width: 70, height: 76) { width height url }
  }
}`;

// Этап 2: конкретный бренд + его устройства (без вариантов)
const brandWithDevicesShape = `{
  id
  name
  devices(filter: { _sort: "name" } ) {
    id
    deviceType { id name }
    name
    preview {
      id
      fit(width: 70, height: 76) { width height url }
    }
  }
}`;

// Этап 3: конкретное устройство + его варианты (только маленькое превью 100x100)
// Большое 300x300 формируется в CutPageWorkView подменой URL
const deviceWithVariantsShape = `{
  id
  name
  variants(filter: { _sort: "name" } ) {
    id
    name
    preview {
      id
      fit(width: 100, height: 100) { width height url }
    }
  }
}`;

const initialState: CutState = {
  brands: null,
  brandData: null,
  brandDataId: null,
  deviceData: null,
  deviceDataId: null,
  selectedVariantId: null,
  currentCut: null,
};

const ACTION_SET_STATE = 'frontdesk/cut/ACTION_SET_STATE';

function setState(stateDelta: Object) {
  return {
    type: ACTION_SET_STATE,
    stateDelta,
  };
}

// === Загрузчики ===

async function loadBrands() {
  try {
    const rawBrands = await listBrands({ _sort: 'name' }, brandsListShape);
    return setState({ brands: rawBrands });
  } catch (err) {
    return setState({
      brands: new ErrorCapsule(err, () => [
        setState({ brands: null }),
        loadBrands(),
      ]),
    });
  }
}

async function loadBrandWithDevices(brandId: string) {
  try {
    const rawBrand = await getBrand(brandId, brandWithDevicesShape);

    if (!rawBrand) {
      return setState({ brandData: null, brandDataId: brandId });
    }

    return setState({ brandData: rawBrand, brandDataId: brandId });
  } catch (err) {
    return setState({
      brandData: new ErrorCapsule(err, () => [
        setState({ brandData: null, brandDataId: null }),
        loadBrandWithDevices(brandId),
      ]),
      brandDataId: brandId,
    });
  }
}

async function loadDeviceWithVariants(deviceId: string) {
  try {
    const rawDevice = await getDevice(deviceId, deviceWithVariantsShape);
    return setState({ deviceData: rawDevice, deviceDataId: deviceId });
  } catch (err) {
    return setState({
      deviceData: new ErrorCapsule(err, () => [
        setState({ deviceData: null, deviceDataId: null }),
        loadDeviceWithVariants(deviceId),
      ]),
      deviceDataId: deviceId,
    });
  }
}

async function print(printerId: string, variantId: string) {
  try {
    const cut = await createCut({ printerId, variantId }, '{ id status }');
    return setState({ currentCut: cut });
  } catch (err) {
    return setState({
      currentCut: new ErrorCapsule(err, () => [
        setState({ currentCut: null }),
        print(printerId, variantId),
      ]),
    });
  }
}

export function cutReducer(
  state: CutState = initialState,
  action: AnyAction
) {
  switch (action.type) {
    case ACTION_SET_STATE:
      return {
        ...state,
        ...action.stateDelta,
      };
    default:
      return state;
  }
}

type Props = {
  brandId?: string,
  deviceId?: string,
}

function CutPage({ brandId, deviceId }: Props) {
  const dispatch = useDispatch();
  const {
    brands,
    brandData,
    brandDataId,
    deviceData,
    deviceDataId,
    selectedVariantId,
    currentCut,
  }: CutState = useSelector(({ frontdesk }) => frontdesk.cut);
  const selectedPrinter = useSelector(getSelectedPrinter);

  // Загружаем бренды один раз при первом заходе на любой /frontdesk/* экран
  useEffect(() => {
    if (!brands) {
      dispatch(loadBrands());
    }
  }, []);

  // Загружаем устройства бренда когда меняется brandId в URL
  useEffect(() => {
    if (brandId && brandId !== brandDataId) {
      dispatch(setState({ brandData: null, brandDataId: null }));
      dispatch(loadBrandWithDevices(brandId));
    }
  }, [brandId]);

  // Загружаем варианты устройства когда меняется deviceId в URL
  useEffect(() => {
    if (deviceId && deviceId !== deviceDataId) {
      dispatch(setState({ deviceData: null, deviceDataId: null, selectedVariantId: null }));
      dispatch(loadDeviceWithVariants(deviceId));
    }
  }, [deviceId]);

  // Сбрасываем currentCut и selectedVariantId при размонтировании
  useEffect(() => () => {
    dispatch(setState({ currentCut: null, selectedVariantId: null }));
  }, []);

  // === Рендер страницы реза ===
  if (deviceId) {
    if (!deviceData || deviceDataId !== deviceId) return <Loader />;
    if (deviceData instanceof ErrorCapsule) return <LoadFailed error={deviceData} />;
    if (!deviceData.variants || deviceData.variants.length === 0) {
      return <Error404Page />;
    }

    if (!brandData || brandDataId !== brandId) return <Loader />;
    if (brandData instanceof ErrorCapsule) return <LoadFailed error={brandData} />;

    const device: Device = deviceData;
    const brand: Brand = brandData;

    const isSelectedGood = selectedVariantId
      && device.variants.find(({ id }) => id === selectedVariantId);

    return (
      <CutPageWorkView
        brand={brand}
        device={device}
        variants={device.variants}
        selectedVariantId={isSelectedGood ? selectedVariantId : device.variants[0].id}
        canPrint={selectedPrinter != null}
        currentCut={currentCut}
        onSelectVariant={(id) => dispatch(setState({ selectedVariantId: id }))}
        onPrint={(variantId) => dispatch(print(selectedPrinter.id, variantId))}
      />
    );
  }

  // === Рендер страницы устройств бренда ===
  if (brandId) {
    if (!brandData || brandDataId !== brandId) return <Loader />;
    if (brandData instanceof ErrorCapsule) return <LoadFailed error={brandData} />;

    return (
      <CutPageDevicesView
        brand={brandData}
        devices={brandData.devices || []}
      />
    );
  }

  // === Рендер списка брендов ===
  if (!brands) return <Loader />;
  if (brands instanceof ErrorCapsule) return <LoadFailed error={brands} />;

  return (
    <CutPageBrandsView
      brands={brands}
    />
  );
}

export function cutPageRoutes(): Routes {
  return [
    {
      title: 'Выбор марки',
      path: '/',
      isEnabled: ({ isUser }) => isUser,
      nav: { showNews: true },
      render: params => (
        <CutPage
          {...sanitizeRouteParams(params, {
          })}
        />
      ),
    },
    {
      title: 'Выбор модели',
      path: '/:brandId/',
      isEnabled: ({ isUser }) => isUser,
      nav: { showNews: true },
      render: params => (
        <CutPage
          {...sanitizeRouteParams(params, {
            brandId: 'id',
          })}
        />
      ),
    },
    {
      title: 'Рез',
      path: '/:brandId/:deviceId/',
      isEnabled: ({ isUser }) => isUser,
      nav: { showNews: true },
      render: params => (
        <CutPage
          {...sanitizeRouteParams(params, {
            brandId: 'id',
            deviceId: 'id',
          })}
        />
      ),
    },
  ];
}